import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from 'helpers/redux';
import { RootState } from 'app/store';
import { CatalogItem, UpdateCatalogRequest } from '../models';
import TextArea from 'components/TextArea/TextArea';
import { IoMdClose } from 'react-icons/io';
import { RiEdit2Line } from 'react-icons/ri';
import { updateCatalogAction } from '../catalog.reducer';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

interface EditCatalogDescriptionModalProps {
    isOpen: boolean;
    onClose: () => void;
    item: CatalogItem;
    onSuccess?: () => void;
}

interface EditDescriptionForm {
    description: string;
}

const schema = yup.object().shape({
    description: yup
        .string()
        .required('Description is required'),
});

const EditCatalogDescriptionModal: React.FC<EditCatalogDescriptionModalProps> = ({
    isOpen,
    onClose,
    item,
    onSuccess,
}) => {
    const dispatch = useAppDispatch();
    const MySwal = withReactContent(Swal);

    const isLoading = useAppSelector(
        (state: RootState) => state.catalog.isLoading ?? false,
    );

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<EditDescriptionForm>({
        resolver: yupResolver(schema) as any,
        defaultValues: {
            description: '',
        },
    });

    useEffect(() => {
        if (isOpen && item) {
            reset({
                description: item.description || '',
            });
        }
    }, [isOpen, item, reset]);

    const onSubmit = async (data: EditDescriptionForm) => {
        const updatePayload: UpdateCatalogRequest = {
            id: item.id,
            title: item.title,
            originalFileName: item.originalFileName || null,
            description: data.description,
            category: item.category,
            teamId: item.teamId || item.team?.id || '',
            version: item.version,
            file: item.file || '',
            status: item.status,
        };

        const res = await dispatch(updateCatalogAction(updatePayload));
        if (updateCatalogAction.fulfilled.match(res)) {
            MySwal.fire({
                title: 'Description Updated!',
                text: 'The catalog description has been successfully updated.',
                icon: 'success',
                confirmButtonColor: '#4c8b6a',
            });
            if (onSuccess) onSuccess();
            onClose();
        } else {
            MySwal.fire({
                title: 'Update Failed',
                text: 'There was an error updating the description. Please try again.',
                icon: 'error',
            });
        }
    };

    if (!isOpen || !item) return null;

    return (
        <div className="fixed inset-0 z-[999] flex items-center justify-end bg-black/50 backdrop-blur-sm transition-opacity duration-300">
            <div className="bg-white dark:bg-gray-800 w-full max-w-lg h-full overflow-y-auto shadow-2xl flex flex-col justify-between animate-slide-left border-l border-gray-100 dark:border-gray-700">
                <div>
                    <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur z-10">
                        <div>
                            <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center">
                                <RiEdit2Line
                                    className="mr-2 text-[#4c8b6a]"
                                    size={24}
                                />
                                Edit Description
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate max-w-[280px]">
                                {item.title}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                            <IoMdClose size={22} />
                        </button>
                    </div>

                    <div className="p-6">
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            className="space-y-4"
                        >
                            <TextArea
                                label="Description / History Perubahan *"
                                placeholder="Edit description..."
                                rows={6}
                                {...register('description')}
                                message={errors.description?.message}
                            />

                            <div className="pt-6 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end space-x-3 sticky bottom-0 bg-white dark:bg-gray-800 pb-2">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="px-5 py-2.5 rounded-lg bg-[#4c8b6a] hover:bg-[#3d7055] text-white font-medium text-sm transition-colors flex items-center justify-center disabled:opacity-50"
                                >
                                    {isLoading
                                        ? 'Saving...'
                                        : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditCatalogDescriptionModal;
